<script setup lang="ts">
import {computed, inject, ref} from "vue";
import {QuestionTypes} from "@/Enums";
import {Avatar, Button, Icon, Tag} from "@/Components/Library";
import CoverImage from "./CoverImage.vue";
import type dayjs from "dayjs";
import Str from "@/Utils/Str";
import {TagList} from "@/Components/Global";

const $date = inject("date") as typeof dayjs;

const {
    group: groupProp,
    question,
    collapsed = false,
    showMenu = false,
} = defineProps<{
    group?: any;
    question: any;
    collapsed?: boolean;
    showMenu?: boolean;
}>();
const emit = defineEmits<{
    menu: [question: any];
    toggle: [key: string, question: any];
}>();

const group = computed(() => groupProp ?? question.group);
const expandTitle = ref(false);
const title = computed(() => {
    if (!expandTitle.value) {
        return Str.of(question.title).truncate(225, "");
    }

    return question.title;
});
const questionType = computed(() =>
    question.type.slug === "text" && question.params.allow_annotations ? QuestionTypes["annotation"] : question.type,
);
const isAnswered = computed(() => question.user_responses?.length > 0);
const allowMultipleTooltip = (): string => {
    if (question.params?.matrix) {
        if (question.params?.allow_multiple) {
            return "Multiple categories per option enabled";
        }

        return "One category per option";
    }

    if (question.params?.allow_multiple) {
        return "Multiple responses enabled";
    }

    return "Single response only";
};
</script>
<template>
    <div
        class="flex grow items-start border-t border-stone-300 py-2 text-sm lg:text-base dark:border-stone-600"
        :class="{
            'border-l-4 border-l-askr-green pl-2 dark:border-l-askr-green': question.is_pinned,
            '!border-t-red-600': question.deleted_at,
        }"
    >
        <router-link
            v-if="group"
            :to="{name: 'feed', params: {group: group.id}}"
            class="flex items-center gap-2 lg:order-first"
        >
            <Avatar
                :label="group.icon ? undefined : group.initials"
                :image="group.icon?.url"
                class="mr--2 h-8 w-8 flex-none !text-sm lg:!text-base"
                :class="[`bg-${group.color?.class}`, group?.color?.class === 'white' ? 'text-black' : 'text-white']"
            />
        </router-link>
        <div class="border--l bg--white relative grow pl-2 pt-2 lg:mb-2">
            <RouterLink
                :to="{
                    name: 'groups.questions.show',
                    params: {
                        group: group?.id,
                        question: question.id,
                    },
                }"
                class="block"
            >
                <div class="mb-2">
                    <div class="mb-1 flex w-full grow flex-wrap items-center gap-3">
                        <router-link
                            v-if="group"
                            :to="{name: 'feed', params: {group: group.id}}"
                            class="flex items-center gap-2 lg:order-first"
                        >
                            <h6
                                class="text-sm font-bold leading-tight text-stone-500 hover:underline dark:text-stone-400"
                            >
                                {{ group.name }}
                            </h6>
                        </router-link>
                        <div
                            class="ml-auto flex items-center justify-center gap-4 text-stone-500 dark:text-stone-400"
                            :class="[`bg--${questionType?.color}`]"
                        >
                            <!--                            <span-->
                            <!--                                v-if="question.type?.slug === 'gatekeeper'"-->
                            <!--                                class="py&#45;&#45;0.5 rounded-full bg-red-100 px-1.5 text-[10px] font-bold text-red-800"-->
                            <!--                            >-->
                            <!--                                KEY-->
                            <!--                            </span>-->
                            <Tag
                                v-if="question.type.slug !== 'post'"
                                :value="isAnswered ? 'Answered' : 'Unanswered'"
                                :class="
                                    isAnswered
                                        ? 'border border-askr-green !bg-transparent !text-askr-green dark:!bg-transparent'
                                        : '!bg-amber-600 text-white'
                                "
                                class="text-xs font-semibold"
                            />
                            <Tag
                                v-if="question.deleted_at"
                                value="Archived"
                                class="border !border-red-600 !bg-red-600 text-xs font-semibold"
                            />
                            <Icon
                                :icon="questionType?.icon ?? 'mage:message-question-mark-round'"
                                width="20"
                                :class="[`text--${questionType?.color}`]"
                            />
                        </div>
                        <Button
                            class="!ring-0"
                            v-if="showMenu"
                            :icon="{
                                icon: 'pepicons-pencil:dots-y',
                                width: '24',
                            }"
                            @click.stop.prevent="emit('menu', question)"
                        />
                    </div>
                    <h5 class="font-medium leading-normal">
                        {{ title }}
                        <button
                            class="hover:underline"
                            v-if="question.title.length > 225 && !expandTitle"
                            @click.stop.prevent="expandTitle = true"
                        >
                            ...
                        </button>
                    </h5>

                    <TagList class="mt-2" :tags="question.tags" v-if="group.can_admin" />
                </div>
                <div class="mb-2" v-auto-animate>
                    <CoverImage :question="question" />
                </div>
                <div
                    class="ml-auto flex grow items-center gap-2 text-xs font-semibold text-stone-500 lg:grow-0 lg:text-sm dark:text-stone-400"
                >
                    <!-- Stats -->
                    <div
                        v-if="
                            question.type.slug === 'post' || group.params.allow_member_posts || !question.publish_from
                        "
                        class="mr-auto flex items-center gap-0.5"
                    >
                        <Icon icon="mdi:calendar-outline" width="16" />
                        <span class="font-medium tracking-tighter lg:tracking-normal">
                            {{ $date(question.created_at).format("DD/MM/YY HH:mm") }}
                        </span>
                    </div>
                    <div v-else class="mr-auto flex items-center gap-2 lg:gap-4">
                        <div class="flex items-center gap-0.5" v-if="question.publish_from">
                            <Icon icon="mdi:calendar-start-outline" width="16" />
                            <span class="font-medium tracking-tighter lg:tracking-normal">
                                {{ $date(question.publish_from).format("DD/MM/YY HH:mm") }}
                            </span>
                        </div>
                        <div class="flex items-center gap-0.5" v-if="question.publish_to">
                            <Icon icon="mdi:calendar-end-outline" width="16" />
                            <span class="font-medium tracking-tighter lg:tracking-normal">
                                {{ $date(question.publish_to).format("DD/MM/YY HH:mm") }}
                            </span>
                        </div>
                    </div>
                    <div
                        class="mr-1 flex items-center gap-1.5 border-r pr-3"
                        @click="emit('menu', question)"
                        v-if="group.can_admin"
                    >
                        <!-- Settings indicators -->
                        <button
                            @click.stop.prevent="$emit('toggle', 'show_charts', question)"
                            class="cursor-pointer hover:opacity-80"
                        >
                            <Icon
                                icon="healthicons:chart-bar-outline-24px"
                                width="16"
                                :class="
                                    question.params?.show_charts
                                        ? 'text-askr-green'
                                        : 'text-stone-400 dark:text-stone-500'
                                "
                                v-tooltip.top="question.params?.show_charts ? 'Charts visible' : 'Charts hidden'"
                            />
                        </button>
                        <button
                            @click.stop.prevent="$emit('toggle', 'show_responses', question)"
                            class="cursor-pointer hover:opacity-80"
                        >
                            <Icon
                                icon="fluent:clipboard-task-list-rtl-24-regular"
                                width="16"
                                :class="
                                    question.params?.show_responses
                                        ? 'text-askr-green'
                                        : 'text-stone-400 dark:text-stone-500'
                                "
                                v-tooltip.top="
                                    question.params?.show_responses ? 'Responses visible' : 'Responses hidden'
                                "
                            />
                        </button>
                        <button
                            @click.stop.prevent="$emit('toggle', 'allow_comments', question)"
                            class="cursor-pointer hover:opacity-80"
                        >
                            <Icon
                                icon="fluent:comment-32-regular"
                                width="16"
                                :class="
                                    question.params?.allow_comments
                                        ? 'text-askr-green'
                                        : 'text-stone-400 dark:text-stone-500'
                                "
                                v-tooltip.top="
                                    question.params?.allow_comments ? 'Comments enabled' : 'Comments disabled'
                                "
                            />
                        </button>
                        <button
                            @click.stop.prevent="$emit('toggle', 'allow_multiple', question)"
                            class="cursor-pointer hover:opacity-80"
                        >
                            <Icon
                                :icon="
                                    question.params?.matrix
                                        ? 'mdi:checkbox-multiple-marked-outline'
                                        : 'mdi:account-multiple-outline'
                                "
                                width="16"
                                :class="
                                    question.params?.allow_multiple
                                        ? 'text-askr-green'
                                        : 'text-stone-400 dark:text-stone-500'
                                "
                                v-tooltip.top="allowMultipleTooltip()"
                            />
                        </button>
                    </div>
                    <div class="flex items-start gap-0.5" :class="{'text-askr-green': question.user_responses?.length}">
                        <Icon icon="fluent:clipboard-task-list-rtl-24-regular" width="16" />
                        <span class="pt-[2px] font-medium leading-none">{{ question.group_responses_count }}</span>
                    </div>
                    <div
                        class="items--center ml-0.5 flex gap-0.5"
                        :class="{'text-askr-green': question.comments?.length}"
                    >
                        <Icon icon="fluent:comment-32-regular" width="16" />
                        <span class="pt-[2px] font-medium leading-none">{{ question.comments_count }}</span>
                    </div>
                </div>
            </RouterLink>
        </div>
    </div>
</template>
<style scoped></style>
